import React, { useState, useEffect } from 'react';
import { X, Clock, Mail, Calendar, Send, Play, Pause, Trash2, Plus, AlertCircle, CheckCircle } from 'lucide-react';
import { createSchedule, updateSchedule, deleteSchedule, toggleSchedule, runScheduleNow, getSchedules, sendTestEmail } from '../../services/reportBuilderService';

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' }
];

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Asia/Tokyo', 'Australia/Sydney', 'America/Toronto', 'America/Sao_Paulo'
];

export default function ScheduleReportModal({ isOpen, onClose, templateId, templateName }) {
  const [tab, setTab] = useState('create');
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [form, setForm] = useState({
    frequency: 'weekly',
    dayOfMonth: 1,
    timeOfDay: '09:00',
    timezone: 'America/New_York',
    recipients: [''],
    subject: '',
    message: '',
    exportFormat: 'pdf'
  });

  useEffect(() => {
    if (isOpen && templateId) {
      loadSchedules();
    }
  }, [isOpen, templateId]);

  const loadSchedules = async () => {
    try {
      const data = await getSchedules();
      const filtered = data.schedules.filter(s => s.templateId === templateId);
      setSchedules(filtered);
      if (filtered.length > 0) setTab('list');
    } catch (err) {
      console.error('Failed to load schedules:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const recipients = form.recipients.filter(r => r.trim());
      if (recipients.length === 0) {
        setMessage({ type: 'error', text: 'Add at least one recipient email' });
        setLoading(false);
        return;
      }
      await createSchedule({
        templateId,
        frequency: form.frequency,
        dayOfMonth: form.dayOfMonth,
        timeOfDay: form.timeOfDay,
        timezone: form.timezone,
        recipients,
        subject: form.subject || null,
        message: form.message || null,
        exportFormat: form.exportFormat
      });
      setMessage({ type: 'success', text: 'Schedule created successfully!' });
      await loadSchedules();
      setTab('list');
      setForm({ frequency: 'weekly', dayOfMonth: 1, timeOfDay: '09:00', timezone: 'America/New_York', recipients: [''], subject: '', message: '', exportFormat: 'pdf' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleSchedule(id);
      await loadSchedules();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to toggle schedule' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await deleteSchedule(id);
      await loadSchedules();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete schedule' });
    }
  };

  const handleRunNow = async (id) => {
    setLoading(true);
    try {
      const result = await runScheduleNow(id);
      setMessage({ type: result.success ? 'success' : 'error', text: result.success ? 'Report sent!' : 'Failed to send' });
      await loadSchedules();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send report' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    const email = form.recipients.find(r => r.trim());
    if (!email) { setMessage({ type: 'error', text: 'Enter an email first' }); return; }
    setLoading(true);
    try {
      const result = await sendTestEmail(email);
      setMessage({ type: result.success ? 'success' : 'error', text: result.success ? 'Test email sent!' : 'Failed to send test' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send test email' });
    } finally {
      setLoading(false);
    }
  };

  const addRecipient = () => setForm(f => ({ ...f, recipients: [...f.recipients, ''] }));
  const removeRecipient = (idx) => setForm(f => ({ ...f, recipients: f.recipients.filter((_, i) => i !== idx) }));
  const updateRecipient = (idx, val) => setForm(f => {
    const r = [...f.recipients]; r[idx] = val; return { ...f, recipients: r };
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-indigo-500 to-purple-600">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5" /> Schedule Report
            </h2>
            <p className="text-indigo-100 text-sm mt-0.5">{templateName || 'Untitled Report'}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button onClick={() => setTab('create')} className={`flex-1 py-3 text-sm font-medium border-b-2 ${tab === 'create' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Plus className="w-4 h-4 inline mr-1" /> New Schedule
          </button>
          <button onClick={() => setTab('list')} className={`flex-1 py-3 text-sm font-medium border-b-2 ${tab === 'list' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <Calendar className="w-4 h-4 inline mr-1" /> Active Schedules ({schedules.length})
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-5 mt-3 p-3 rounded-lg flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'create' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Frequency & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                  <select value={form.frequency} onChange={e => setForm(f => ({...f, frequency: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time of Day</label>
                  <input type="time" value={form.timeOfDay} onChange={e => setForm(f => ({...f, timeOfDay: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>

              {(form.frequency === 'monthly' || form.frequency === 'quarterly') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                  <input type="number" min="1" max="28" value={form.dayOfMonth} onChange={e => setForm(f => ({...f, dayOfMonth: parseInt(e.target.value)}))} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select value={form.timezone} onChange={e => setForm(f => ({...f, timezone: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                </select>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" /> Recipients
                </label>
                {form.recipients.map((r, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input type="email" placeholder="email@example.com" value={r} onChange={e => updateRecipient(idx, e.target.value)} className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" required />
                    {form.recipients.length > 1 && (
                      <button type="button" onClick={() => removeRecipient(idx)} className="text-red-400 hover:text-red-600 px-2"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addRecipient} className="text-indigo-600 text-sm hover:text-indigo-700 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add recipient
                </button>
              </div>

              {/* Subject & Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject (optional)</label>
                <input type="text" value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder={`SERPfinity Report: ${templateName || 'Report'}`} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Message (optional)</label>
                <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} rows="2" placeholder="Add a personal message to the email..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Export Format</label>
                <select value={form.exportFormat} onChange={e => setForm(f => ({...f, exportFormat: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                  <option value="both">PDF + CSV</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={loading} className="flex-1 bg-indigo-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4" /> {loading ? 'Creating...' : 'Create Schedule'}
                </button>
                <button type="button" onClick={handleTestEmail} disabled={loading} className="px-4 bg-gray-100 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2">
                  <Send className="w-4 h-4" /> Test Email
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              {schedules.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No active schedules</p>
                  <p className="text-sm mt-1">Create a schedule to automatically send this report</p>
                </div>
              ) : (
                schedules.map(s => (
                  <div key={s.id} className={`border rounded-lg p-4 ${s.status === 'active' ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {s.status}
                        </span>
                        <span className="text-sm font-medium text-gray-900 capitalize">{s.frequency}</span>
                        <span className="text-xs text-gray-500">at {s.timeOfDay || '09:00'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleRunNow(s.id)} title="Send now" className="p-1.5 rounded hover:bg-white text-indigo-600"><Send className="w-4 h-4" /></button>
                        <button onClick={() => handleToggle(s.id)} title={s.status === 'active' ? 'Pause' : 'Resume'} className="p-1.5 rounded hover:bg-white text-gray-600">
                          {s.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleDelete(s.id)} title="Delete" className="p-1.5 rounded hover:bg-white text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <p><Mail className="w-3 h-3 inline mr-1" />{(s.recipients || []).join(', ')}</p>
                      {s.nextRunAt && <p><Calendar className="w-3 h-3 inline mr-1" />Next: {new Date(s.nextRunAt).toLocaleString()}</p>}
                      {s.lastRunAt && <p>Last run: {new Date(s.lastRunAt).toLocaleString()} ({s.lastRunStatus || 'unknown'})</p>}
                      <p>Total runs: {s.runCount || 0}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
